"use client";
import React from "react";
import { usePathname } from "next/navigation";
import { Home, Search, Tag, Package, Download } from "lucide-react";
import Link from "components/common/Link";
import { useT } from "providers/I18nProvider";
import useStoreUrl from "hooks/useStoreUrl";
import { stripLocale } from "utils/locale";
import { FEATURE_PREPAID_PACKAGE } from "utils/constant";

/**
 * Thanh tab điều hướng dính đáy màn hình (chỉ mobile < md), luôn hiển thị như app.
 * 5 tab: Trang chủ · Tìm kiếm · Ưu đãi · Gói tiết kiệm · Tải App (mở thẳng store).
 * Trang chi tiết shop KHÔNG hiện (đã có thanh Đặt lịch dính đáy, tránh chồng 2 thanh).
 * Nội dung trang được chừa đáy bằng padding ở <body> (globals.scss .has-tabbar).
 */
const TABS = [
  { key: "home", href: "/", icon: Home, labelKey: "tabbar.home", match: (p: string) => p === "/" },
  { key: "search", href: "/search", icon: Search, labelKey: "tabbar.search", match: (p: string) => p.startsWith("/search") },
  { key: "offers", href: "/uu-dai", icon: Tag, labelKey: "tabbar.offers", match: (p: string) => p.startsWith("/uu-dai") },
  { key: "packages", href: "/goi-tiet-kiem", icon: Package, labelKey: "tabbar.packages", match: (p: string) => p.startsWith("/goi-tiet-kiem") },
].filter((tab) => FEATURE_PREPAID_PACKAGE || tab.key !== "packages");

// Các route "tĩnh" của site; ngoài danh sách này (và không phải /) là slug shop → ẩn tab bar
const STATIC_PREFIXES = ["/search", "/uu-dai", "/goi-tiet-kiem", "/hop-tac", "/dang-nhap", "/chinh-sach"];

export const isShopDetailPath = (path: string) => {
  const p = stripLocale(path) || "/";
  if (p === "/") return false;
  return !STATIC_PREFIXES.some((s) => p.startsWith(s));
};

const BottomTabBar = () => {
  const t = useT();
  const pathname = usePathname() || "/";
  const storeUrl = useStoreUrl();
  const path = stripLocale(pathname) || "/";

  if (isShopDetailPath(pathname)) return null;

  const base =
    "flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[50px] text-[12px] leading-none font-medium select-none";

  return (
    <nav
      data-tabbar=""
      aria-label={t("tabbar.ariaLabel")}
      className="md:hidden fixed inset-x-0 bottom-0 z-[900] border-t border-gray-100 bg-white/95 backdrop-blur shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-stretch px-1">
        {TABS.map(({ key, href, icon: Icon, labelKey, match }) => {
          const active = match(path);
          return (
            <Link
              key={key}
              href={href}
              aria-current={active ? "page" : undefined}
              className={`${base} ${active ? "text-primary" : "text-gray-500"}`}
            >
              <Icon size={22} strokeWidth={active ? 2.4 : 1.8} />
              <span>{t(labelKey)}</span>
            </Link>
          );
        })}
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${base} text-gray-500`}
        >
          <span className="flex h-[22px] items-center justify-center">
            <span className="flex h-7 w-7 -mt-1 items-center justify-center rounded-full bg-primary text-white">
              <Download size={15} strokeWidth={2.4} />
            </span>
          </span>
          <span className="text-primary">{t("tabbar.app")}</span>
        </a>
      </div>
    </nav>
  );
};

export default BottomTabBar;
